import React from 'react';
import gatherChangelog from './gather-using-ai.changes.md';

import { v4 as uuidv4 } from 'uuid';
import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useSelector } from 'react-redux';
import { Command, selectCommandByNodeId, State, tabActions } from '@store/index';
import { Stack } from '@mui/material';
import { NodeWebhookAnchors } from '@common/Node/Defaults/NodeWebhookAnchors';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';
import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { AnchorType } from '@components/Flow';
import { googleLanguageOptions } from '../TranscriptionStart/languages';

import {
  GatherUsingAINodeState,
  NodeConfig,
  NodeField,
  NodeFieldType,
  NodeInputType,
  NodeOutput,
  NodeType,
} from '../..';

export const config: NodeConfig = {
  version: '1.4',
  title: 'Gather Using AI',
  identifier: 'GatherUsingAI',
  command_id: 'call.ai_gather',
  icon: 'telnyx',
  background: '#000000',
  changelog: gatherChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'call.ai_gather.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'call.ai_gather.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.ai_gather.ended',
      label: 'call.ai_gather.ended',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.ai_gather.ended.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.ai_gather.partial_results',
      label: 'call.ai_gather.partial_results',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.ai_gather.partial_results.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
  webhooks: [
    {
      webhook: 'call.ai_gather.ended',
      expected: true,
      multi: false,
    },
    {
      webhook: 'call.ai_gather.partial_results',
      expected: false,
      multi: false,
    },
  ],
};

export const state: GatherUsingAINodeState = {
  model: '',
  call_control_id: { type: NodeInputType.Expression, value: '' },
  voice: { type: NodeInputType.Option, value: 'Telnyx.KokoroTTS' },
  args: [
    {
      id: uuidv4(),
      name: '',
      description: '',
      enumerator: '',
      type: 'string',
    },
  ],
  instructions: { type: NodeInputType.Expression, value: '' },
  message_history: { type: NodeInputType.Expression, value: '' },
  client_state: { type: NodeInputType.Expression, value: '' },
  greeting: { type: NodeInputType.Expression, value: '' },
  gather_ended_speech: { type: NodeInputType.Expression, value: '' },
  voice_speed: { type: NodeInputType.Number, value: '1' },
  elevenlabs_api_key: '',
  elevenlabs_voice_id: '',
  elevenlabs_model_id: 'eleven_turbo_v2',
  kokoro_voice_id: 'af_bella',
  azure_voice_id: 'en-US-AvaMultilingualNeural',
  aws_polly_voice_id: 'Joanna',
  aws_polly_neural: 'standard',
  send_partial_results: 'false',
  interruption_enabled: 'true',
  transcription_model: 'distil-whisper/distil-large-v2',
  language_google: 'en',
};

export const fields: NodeField[] = [
  {
    id: 'call_control_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Call Control ID',
    description: 'Unique identifier and token for controlling the call',
    placeholder: 'A unique identifier for controlling the call',
    required: false,
  },
  {
    id: 'args',
    type: NodeFieldType.ToolArgs,
    label: 'Arguments',
    description:
      'The parameters described as a JSON Schema object that needs to be gathered by the voice assistant. See the JSON Schema reference for documentation about the format',
  },
  {
    id: 'model',
    tab: '1',
    type: NodeFieldType.Option,
    label: 'Model',
    description: 'The large language model to use.',
    placeholder: 'openai/gpt-4o',
    required: true,
  },
  {
    id: 'instructions',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Instructions',
    description: 'Optionally redefine the system instructions that the voice assistant uses during the gather command.',
    placeholder: 'Write instructions for the LLM to gather information',
    required: false,
  },
  {
    id: 'greeting',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Greeting',
    description:
      'Text that will be played when the gathering starts, if none then nothing will be played when the gathering starts. The greeting can be text or SSML. There is a 3,000 character limit.',
    placeholder: 'Text that will be played when the gathering starts.',
    required: false,
  },
  {
    id: 'message_history',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Message History',
    description:
      'Optionally, the message history you want the voice assistant to be aware of, this can be useful to keep the context of the conversation, or to pass additional information to the voice assistant. This needs to be formatted as a JSON array, please ensure you follow the format defined in the Telnyx API documentation.',
    placeholder: 'Message history from previous Gather.',
    required: false,
  },
  {
    id: 'client_state',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Client State',
    description: 'Use this field to add state to every subsequent webhook. It must be a valid Base-64 encoded string.',
    placeholder: 'Add state to each subsequent webhook.',
    required: false,
  },
  {
    id: 'send_partial_results',
    tab: 'partial_results',
    type: NodeFieldType.Toggle,
    label: 'Send Partial Results',
    description:
      'If enabled, the voice assistant will send partial results via the call.ai_gather.partial_results callback in real time as individual fields are gathered. If set to false, the voice assistant will only send the final result via the call.ai_gather.ended callback.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
    required: false,
  },
  {
    id: 'transcription_model',
    tab: 'transcription',
    type: NodeFieldType.Option,
    label: 'Transcription Model',
    description:
      'The speech to text model to be used by the voice assistant. distil-whisper/distil-large-v2 is lower latency but English-only. openai/whisper-large-v3-turbo is multi-lingual but slightly higher latency. Google STT provides high-quality speech recognition powered by Google.',
    options: [
      { label: 'distil-whisper/distil-large-v2', value: 'distil-whisper/distil-large-v2' },
      { label: 'openai/whisper-large-v3-turbo', value: 'openai/whisper-large-v3-turbo' },
      { label: 'google stt', value: 'google' },
    ],
    required: false,
  },
  {
    id: 'language_google',
    tab: 'transcription',
    type: NodeFieldType.Option,
    label: 'Google STT Language',
    description: 'The language to be used for Google Speech-to-Text transcription.',
    options: googleLanguageOptions,
    required: false,
  },
  {
    id: 'interruption_enabled',
    tab: 'interruption',
    type: NodeFieldType.Toggle,
    label: 'Allow Interruption',
    description: 'When enabled, allows users to interrupt the assistant while speaking.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
    required: false,
  },
  // Disabled because it is no longer listed in the API documentation and not supported
  // {
  //   id: 'gather_ended_speech',
  //   tab: '1',
  //   type: NodeFieldType.InputExpression,
  //   default_type: NodeInputType.Expression,
  //   label: 'Gather Ended Speech',
  //   description:
  //     'Text that will be played when the gathering is done, if none then nothing will be played when the gathering ends. The gather_ended_speech can be text or SSML. There is a 3,000 character limit.',
  //   placeholder: 'Text that will be played when the gathering is done.',
  //   required: false,
  // },
  {
    id: 'voice',
    tab: '2',
    type: NodeFieldType.OptionExpression,
    label: 'Voice',
    description:
      'The voice to be used by the voice assistant. Currently we support ElevenLabs, Telnyx and AWS voices only, for ElevenLabs voices you can pass the voice as ElevenLabs.<model_id>.<voice_id>, for Telnyx voices you can pass the voice as Telnyx.<model_id>.<voice_id>, for AWS Polly voices you can pass the voice as AWS.Polly.<voice_id>',
    required: false,
    options: [
      { label: 'Telnyx.KokoroTTS', value: 'Telnyx.KokoroTTS' },
      { label: 'Telnyx.LibriTTS.0', value: 'Telnyx.LibriTTS.0' },
      { label: 'ElevenLabs', value: 'ElevenLabs' },
      { label: 'AWS.Polly', value: 'AWS.Polly' },
      { label: 'Azure', value: 'Azure' },
    ],
  },
  {
    id: 'voice_speed',
    tab: 'telnyx_voice',
    type: NodeFieldType.NumberExpression,
    label: 'Voice Speed',
    description:
      'The voice speed to be used for the voice. The voice speed must be between 0.1 and 2.0. Default value is 1.0.',
    required: false,
    minNumber: 0.1,
    maxNumber: 2,
  },
  {
    id: 'elevenlabs_api_key',
    tab: 'elevenlabs_voice',
    type: NodeFieldType.Secret,
    label: 'Eleven Labs API Key',
    description:
      'The secret that contains your ElevenLabs API key. If you have not created a secret yet, you can do so from the workspace tab named Secrets.',
    required: false,
  },
  {
    id: 'elevenlabs_voice_id',
    tab: 'elevenlabs_voice',
    type: NodeFieldType.Option,
    label: 'ElevenLabs Voice',
    description:
      'ElevenLabs voices, identified by voice IDs, determine the underlying speech synthesis technology used to convert text into speech.',
    required: false,
    placeholder: 'The voice model to use to convert text-to-speech.',
  },
  {
    id: 'elevenlabs_model_id',
    type: NodeFieldType.Option,
    label: 'ElevenLabs Voice',
    description:
      'ElevenLabs models, identified by model IDs, determine the underlying capabilites of the voice model such as the languages they can speak in.',
    required: false,
    placeholder: 'The model to use to convert text-to-speech.',
  },
  {
    id: 'aws_polly_voice_id',
    tab: 'aws_voice',
    type: NodeFieldType.Option,
    label: 'AWS Polly Voice',
    description: 'An AWS.Polly voice.',
    required: false,
    placeholder: 'The AWS.Polly voice used to convert text-to-speech.',
  },
  {
    id: 'aws_polly_neural',
    tab: 'aws_polly_settings',
    type: NodeFieldType.Toggle,
    label: 'AWS Polly Voice Type',
    description: `Only certain voices support different voice types. Enable the neural setting to produce higher quality voices than the standard voices. Enable generative voices for the most human-like, emotionally engaged, and adaptive conversational voices available. Enable the long form setting to produce human-like, highly expressive, and emotionally adept voices.`,
    required: false,
    options: [
      { label: 'Standard', value: 'standard' },
      { label: 'Neural', value: 'neural' },
      { label: 'Generative', value: 'generative' },
      { label: 'Long Form', value: 'long_form' },
    ],
  },
  {
    id: 'kokoro_voice_id',
    tab: 'kokoro_voice',
    type: NodeFieldType.Option,
    label: 'Kokoro Voice Type',
    description: 'The Kokoro voice used to convert text-to-speech.',
    required: false,
    placeholder: 'The voice model to use to convert text-to-speech.',
  },
  {
    id: 'azure_voice_id',
    tab: 'azure_voice',
    type: NodeFieldType.Option,
    label: 'Azure Voice Type',
    description: 'The Azure voice used to convert text-to-speech.',
    required: false,
    placeholder: 'The voice model to use to convert text-to-speech.',
  },
];

export const output: NodeOutput = {
  default: {
    'call.ai_gather.ended': {
      call: {
        ai_gather: {
          ended: {
            call_control_id: 'string',
            call_leg_id: 'string',
            call_session_id: 'string',
            client_state: 'string | null',
            connection_id: 'number',
            from: 'string',
            to: 'string',
            message_history: [
              {
                content: 'Hello, how can I help?',
                role: 'assistant',
              },
              {
                content: 'I need help with Telnyx',
                role: 'user',
              },
            ],
            result: 'object',
            status: 'string',
          },
        },
      },
    },
    'call.ai_gather.partial_results': {
      call: {
        ai_gather: {
          partial_results: {
            call_control_id: 'string',
            call_leg_id: 'string',
            call_session_id: 'string',
            client_state: 'string | null',
            connection_id: 'number',
            from: 'string',
            to: 'string',
            message_history: [
              {
                content: 'Hello, how can I help?',
                role: 'assistant',
              },
              {
                content: 'I need help with Telnyx',
                role: 'user',
              },
            ],
            partial_results: 'object',
          },
        },
      },
    },
  },
  dynamic: null,
};

export const GatherUsingAI: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'call.ai_gather') return;

  const state = command.state as GatherUsingAINodeState;
  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeWebhookAnchors id={id} command={command} data={data} anchors={getGatherUsingAIAnchors(anchors, state)} />
    </NodeContainer>
  );
};

const getGatherUsingAIAnchors = (anchors: AnchorType[], state: GatherUsingAINodeState) => {
  const isPartialResultsEnabled = state?.send_partial_results === 'true';

  return anchors.filter((anchor) => {
    if (!isPartialResultsEnabled && anchor.id.includes('call.ai_gather.partial_results')) return false;
    return true;
  });
};

export const customGatherUsingAIBehaviour = (
  value: string | string[],
  field: NodeField,
  command: Command,
  dispatch: Dispatch<AnyAction>
) => {
  const nodeId = command.node.id;

  if (field.id === 'send_partial_results') {
    if (value === 'true') {
      dispatch(tabActions.nodeWidthUpdate({ nodeId, width: 350 }));
    }

    if (value === 'false') {
      dispatch(tabActions.nodeWidthUpdate({ nodeId, width: 300 }));

      const edgesToRemove = ['call.ai_gather.partial_results', 'call.ai_gather.partial_results.output_data'];
      for (const edge of edgesToRemove)
        dispatch(tabActions.removeEdgeByHandle({ node_id: command.node.id, handle: edge }));
    }
  }
};

export default { config, state, fields, output };
